<template>
  <q-page padding>
    <div class="q-pa-md">
      <div class="row q-col-gutter-md q-mb-lg">
        <div class="col-12 col-md-4">
          <q-card class="bg-primary text-white">
            <q-card-section>
              <div class="text-h6">Total Clicks</div>
              <div class="text-h3">{{ stats.totalClicks }}</div>
            </q-card-section>
          </q-card>
        </div>
        <div class="col-12 col-md-4">
          <q-card class="bg-secondary text-white">
            <q-card-section>
              <div class="text-h6">Total Signups</div>
              <div class="text-h3">{{ stats.totalSignups }}</div>
            </q-card-section>
          </q-card>
        </div>
        <div class="col-12 col-md-4">
          <q-card class="bg-accent text-white">
            <q-card-section>
              <div class="text-h6">Conversion Rate</div>
              <div class="text-h3">{{ stats.conversionRate.toFixed(2) }}%</div>
            </q-card-section>
          </q-card>
        </div>
      </div>
      <div class="row q-col-gutter-md q-mb-lg">
        <div class="col-12 col-md-6">
          <q-card class="bg-accent text-white">
            <q-card-section>
              <div class="text-h6">Total Paid Signups</div>
              <div class="text-h3">{{ stats.totalPaidSignups }}</div>
            </q-card-section>
          </q-card>
        </div>
        <div class="col-12 col-md-6">
          <q-card class="bg-secondary text-white">
            <q-card-section>
              <div class="text-h6">Retention Rate (Paid signups)</div>
              <div class="text-h3">{{ stats.totalPaidConversionRate.toFixed(2) }}%</div>
            </q-card-section>
          </q-card>
        </div>
      </div>

      <div class="row q-mb-md">
        <div class="col-12">
          <div class="flex justify-between items-center">
            <h4 class="q-my-none">Your Affiliate Links</h4>
            <q-btn
              color="primary"
              label="Create New Link"
              icon="mdi-plus"
              :loading="isLoading"
              @click="showCreateDialog = true"
            />
          </div>
        </div>
      </div>

      <q-table
        :rows="stats.links"
        :columns="columns"
        row-key="id"
        :loading="isLoading"
        :pagination="{ rowsPerPage: 10 }"
      >
        <template v-slot:loading>
          <q-inner-loading showing color="primary">
            <q-spinner size="50px" color="primary" />
          </q-inner-loading>
        </template>

        <template v-slot:body-cell-actions="props">
          <q-td :props="props">
            <q-btn flat round color="primary" icon="mdi-content-copy" @click="copyLink(props.row.code)">
              <q-tooltip>Copy Link</q-tooltip>
            </q-btn>
          </q-td>
        </template>
      </q-table>

      <!-- Create New Link Dialog -->
      <q-dialog v-model="showCreateDialog" persistent>
        <q-card style="min-width: 350px">
          <q-card-section>
            <div class="text-h6">Create New Affiliate Link</div>
          </q-card-section>

          <q-card-section class="q-pt-none">
            <q-input
              v-model="newLinkName"
              label="Link Name"
              :rules="[(val) => !!val || 'Name is required']"
              autofocus
              @keyup.enter="createNewLink"
            />
          </q-card-section>

          <q-card-actions align="right" class="text-primary">
            <q-btn flat label="Cancel" v-close-popup />
            <q-btn flat label="Create" :loading="isLoading" @click="createNewLink" />
          </q-card-actions>
        </q-card>
      </q-dialog>

      <!-- Copy Success Dialog -->
      <q-dialog v-model="showCopySuccess">
        <q-card>
          <q-card-section class="row items-center">
            <q-avatar icon="check" color="positive" text-color="white" />
            <span class="q-ml-sm">Link copied to clipboard!</span>
          </q-card-section>
        </q-card>
      </q-dialog>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useAffiliateStore } from '@/stores/affiliateStore'
import { copyAffiliateLink } from '@/utils/affiliate'
import { useQuasar } from 'quasar'
import type { AffiliateLink } from '@/stores/affiliateStore'

const $q = useQuasar()
const affiliateStore = useAffiliateStore()
const showCopySuccess = ref(false)
const showCreateDialog = ref(false)
const newLinkName = ref('')

const isLoading = computed(() => affiliateStore.isLoading)
const stats = computed(() => affiliateStore.stats)

interface TableColumn {
  name: string
  label: string
  field: string | ((row: AffiliateLink) => number)
  required?: boolean
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
  format?: (val: unknown) => string
}

const columns: TableColumn[] = [
  {
    name: 'name',
    required: true,
    label: 'Name',
    align: 'left',
    field: 'name',
    sortable: true
  },
  {
    name: 'code',
    required: true,
    label: 'Affiliate Code',
    align: 'left',
    field: 'code',
    sortable: true
  },
  {
    name: 'createdAt',
    align: 'left',
    label: 'Created',
    field: 'createdAt',
    sortable: true,
    format: (val: unknown) => new Date(val as string).toLocaleDateString()
  },
  {
    name: 'totalClicks',
    align: 'center',
    label: 'Clicks',
    field: 'totalClicks',
    sortable: true
  },
  {
    name: 'totalSignups',
    align: 'center',
    label: 'Signups',
    field: 'totalSignups',
    sortable: true
  },
  {
    name: 'totalPaidSignups',
    align: 'center',
    label: 'Paid',
    field: 'totalPaidSignups',
    sortable: true
  },
  {
    name: 'conversionRate',
    align: 'center',
    label: 'Conversion Rate',
    field: (row: AffiliateLink) => (row.totalClicks > 0 ? (row.totalSignups / row.totalClicks) * 100 : 0),
    format: (val: unknown) => `${(val as number).toFixed(2)}%`,
    sortable: true
  },
  {
    name: 'totalPaidConversionRate',
    align: 'center',
    label: 'Paid Rate',
    field: (row: AffiliateLink) => (row.totalClicks > 0 ? (row.totalPaidSignups / row.totalSignups) * 100 : 0),
    format: (val: unknown) => `${(val as number).toFixed(2)}%`,
    sortable: true
  },
  {
    name: 'lastClickedAt',
    align: 'left',
    label: 'Last Click',
    field: 'lastClickedAt',
    sortable: true,
    format: (val: unknown) => (val ? new Date(val as string).toLocaleDateString() : 'Never')
  },
  {
    name: 'actions',
    align: 'center',
    label: 'Actions',
    field: 'actions'
  }
]

async function createNewLink() {
  if (!newLinkName.value) {
    $q.notify({
      color: 'negative',
      message: 'Please enter a name for the affiliate link',
      icon: 'mdi-alert-circle'
    })
    return
  }

  const result = await affiliateStore.createAffiliateLink(newLinkName.value)
  if (result) {
    showCreateDialog.value = false
    newLinkName.value = ''
    $q.notify({
      color: 'positive',
      message: 'New affiliate link created successfully',
      icon: 'mdi-check'
    })
  }
}

async function copyLink(code: string) {
  const success = await copyAffiliateLink(code)
  if (success) {
    $q.notify({
      color: 'positive',
      message: 'Link copied to clipboard',
      icon: 'mdi-content-copy'
    })
  } else {
    $q.notify({
      color: 'negative',
      message: 'Failed to copy link',
      icon: 'mdi-alert-circle'
    })
  }
}

onMounted(async () => {
  await affiliateStore.fetchAffiliateLinks()
})
</script>

<style scoped>
/* Add any custom styles here */
</style>
